/**
 * The shared visitor cookie behind the marketing → app funnel.
 *
 * The cookie is written on the **parent** domain (`.thesukha.co`) so that
 * www.thesukha.co and app.thesukha.co see the same value. The marketing site
 * does not write it; instead it forwards what it knows as query params on
 * every app-bound link (`sukha_page`, `sukha_cta`, `sukha_entry`, `sukha_ref`),
 * and the app folds those into the cookie on arrival. A later handoff wins
 * over an earlier bare visit, because the question the source fields answer
 * is "which page sent them this time" — while the visitor id and the
 * first-touch referrer are kept, because those describe the visitor.
 */

import { getToltSignupUnsafeMetadata, TOLT_SIGNUP_METADATA_KEY } from "@sukha/shared";
import { LANDING_COOKIE_NAME } from "@sukha/shared/domain/landingSignup";

export { LANDING_COOKIE_NAME };

/** Query params the marketing site forwards on app-bound links. */
export const LANDING_HANDOFF_PARAMS = {
  page: "sukha_page",
  cta: "sukha_cta",
  referrer: "sukha_ref",
  entry: "sukha_entry",
} as const;

/** A year — long enough to survive a trial and the decision that follows it. */
const LANDING_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

const PARENT_DOMAIN = "thesukha.co";

export interface LandingCookieValue {
  /** Anonymous visitor id, stitched to the member's id once they credential. */
  visitorId: string;
  referrerUrl: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  /** The marketing entry page when forwarded, otherwise the app path landed on. */
  landingPath: string | null;
  /** Marketing page the visitor clicked through from. Null on a direct visit. */
  sourcePage: string | null;
  /** Call-to-action on that page. Null when the site did not stamp one. */
  sourceCta: string | null;
  /** Referrer the marketing site saw, when it forwarded one. */
  sourceReferrer: string | null;
  /**
   * Which sign-up button was pressed, written at click time. The server
   * reads it from the request when the account is created, which is the
   * only path that survives Clerk's transfer flow (a new Google user is
   * created from a sign-in attempt, which carries no metadata).
   */
  signupMethod?: "google" | "email" | null;
  /** Tolt's partner and click ids, copied from tolt.js at the same moment, for the same reason. */
  toltPartnerId?: string | null;
  toltClickId?: string | null;
  /** ISO timestamp of first arrival. */
  landedAt: string;
}

/** What one marketing → app hop carried in the URL. */
export interface LandingHandoff {
  sourcePage: string;
  sourceCta: string | null;
  sourceReferrer: string | null;
  entryPath: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
}

function isLandingCookieValue(value: unknown): value is LandingCookieValue {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Partial<LandingCookieValue>;

  return (
    typeof candidate.visitorId === "string" &&
    typeof candidate.landedAt === "string"
  );
}

/** Cookies written before the source fields existed are read as "no source". */
function withSourceDefaults(value: LandingCookieValue): LandingCookieValue {
  return {
    ...value,
    sourcePage: value.sourcePage ?? null,
    sourceCta: value.sourceCta ?? null,
    sourceReferrer: value.sourceReferrer ?? null,
  };
}

/**
 * The widest domain we are allowed to set a cookie on.
 *
 * On `app.thesukha.co` that is `.thesukha.co`, so the marketing site can read
 * it. Anywhere else — localhost, preview deployments, custom hosts — the
 * browser rejects a cross-site domain, so the cookie stays host-only.
 */
function landingCookieDomain(): string | null {
  const { hostname } = window.location;

  return hostname === PARENT_DOMAIN || hostname.endsWith(`.${PARENT_DOMAIN}`)
    ? `.${PARENT_DOMAIN}`
    : null;
}

/**
 * A visitor id, without assuming a secure context.
 *
 * `crypto.randomUUID` only exists on https and localhost, so on a plain-http
 * preview host it is undefined and would throw. The fallback is not
 * cryptographically strong and does not need to be — this identifies an
 * anonymous visitor for attribution, it is not a secret or a session token.
 */
function newVisitorId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function readLandingCookie(): LandingCookieValue | null {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${LANDING_COOKIE_NAME}=`));

  if (!match) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(
      decodeURIComponent(match.slice(LANDING_COOKIE_NAME.length + 1))
    );
    return isLandingCookieValue(parsed) ? withSourceDefaults(parsed) : null;
  } catch {
    return null;
  }
}

/**
 * The landing record as a request field, for `POST /api/v1/user/create`.
 *
 * The cookie reached the API only while the browser called it through the web
 * rewrite. Calling the API host directly sends no cookies, so the value goes in
 * the body instead; the API still reads the cookie when the body has none.
 *
 * @returns `{ landing }` when this browser has a landing record, otherwise `{}`
 */
export function landingRequestField(): { landing?: LandingCookieValue } {
  const landing = readLandingCookie();
  return landing ? { landing } : {};
}

/**
 * Longest value kept from each handoff param. These match the server's
 * schemas: a value the server would refuse must be cut here rather than
 * carried, because one overlong CTA slug on a marketing link would otherwise
 * void the whole attribution POST for every visitor who clicked it.
 */
const PARAM_MAX_CHARS: Record<string, number> = {
  [LANDING_HANDOFF_PARAMS.page]: 512,
  [LANDING_HANDOFF_PARAMS.cta]: 64,
  [LANDING_HANDOFF_PARAMS.referrer]: 2048,
  [LANDING_HANDOFF_PARAMS.entry]: 2048,
};

/** A param's value, with an empty string read as absent and an oversized one cut. */
function param(params: URLSearchParams, name: string): string | null {
  const value = params.get(name);
  const max = PARAM_MAX_CHARS[name] ?? 255;
  return value && value.length > 0 ? value.slice(0, max) : null;
}

/**
 * The marketing handoff carried by a URL, if it carries one.
 *
 * `sukha_page` is the marker: without it there was no handoff and the other
 * params are ignored, so a stray `utm_source` on a bookmarked app link cannot
 * rewrite a source the marketing site actually recorded.
 *
 * @param search - The query string to read, e.g. `window.location.search`
 */
export function readLandingHandoff(search: string): LandingHandoff | null {
  const params = new URLSearchParams(search);
  const sourcePage = param(params, LANDING_HANDOFF_PARAMS.page);

  if (!sourcePage) {
    return null;
  }

  return {
    sourcePage,
    sourceCta: param(params, LANDING_HANDOFF_PARAMS.cta),
    sourceReferrer: param(params, LANDING_HANDOFF_PARAMS.referrer),
    entryPath: param(params, LANDING_HANDOFF_PARAMS.entry),
    utmSource: param(params, "utm_source"),
    utmMedium: param(params, "utm_medium"),
    utmCampaign: param(params, "utm_campaign"),
    utmContent: param(params, "utm_content"),
    utmTerm: param(params, "utm_term"),
  };
}

/**
 * Fold a new handoff into an existing cookie.
 *
 * The visitor id, first-arrival time and first-touch referrer describe the
 * visitor and are kept. The source page and CTA describe this hop and are
 * replaced. The entry path and UTM values are replaced only when the new hop
 * carries them, so a handoff without `sukha_entry` does not erase one that had
 * it.
 *
 * Exported for tests; production code goes through {@link ensureLandingCookie}.
 */
export function mergeLandingHandoff(
  existing: LandingCookieValue,
  handoff: LandingHandoff
): LandingCookieValue {
  return {
    ...existing,
    sourcePage: handoff.sourcePage,
    sourceCta: handoff.sourceCta,
    sourceReferrer: handoff.sourceReferrer ?? existing.sourceReferrer,
    landingPath: handoff.entryPath ?? existing.landingPath,
    utmSource: handoff.utmSource ?? existing.utmSource,
    utmMedium: handoff.utmMedium ?? existing.utmMedium,
    utmCampaign: handoff.utmCampaign ?? existing.utmCampaign,
    utmContent: handoff.utmContent ?? existing.utmContent,
    utmTerm: handoff.utmTerm ?? existing.utmTerm,
  };
}

/**
 * Build the landing record for the page currently being viewed.
 *
 * Exported for the sake of tests; production code goes through
 * {@link ensureLandingCookie}, which only calls this on a first arrival.
 */
export function buildLandingCookieValue(
  visitorId: string,
  landedAt: string
): LandingCookieValue {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || null;
  const handoff = readLandingHandoff(window.location.search);

  return {
    visitorId,
    // Our own pages are not a referrer worth recording — the member was
    // already here.
    referrerUrl:
      referrer && !referrer.startsWith(window.location.origin)
        ? referrer
        : null,
    utmSource: params.get("utm_source"),
    utmMedium: params.get("utm_medium"),
    utmCampaign: params.get("utm_campaign"),
    utmContent: params.get("utm_content"),
    utmTerm: params.get("utm_term"),
    // The marketing site's entry page is the true landing; the app path is
    // only the last hop.
    landingPath: handoff?.entryPath ?? window.location.pathname,
    sourcePage: handoff?.sourcePage ?? null,
    sourceCta: handoff?.sourceCta ?? null,
    sourceReferrer: handoff?.sourceReferrer ?? null,
    landedAt,
  };
}

function writeLandingCookie(value: LandingCookieValue): void {
  const domain = landingCookieDomain();

  try {
    document.cookie = [
      `${LANDING_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}`,
      "path=/",
      `max-age=${LANDING_COOKIE_MAX_AGE_SECONDS}`,
      "SameSite=Lax",
      ...(domain ? [`domain=${domain}`] : []),
      ...(window.location.protocol === "https:" ? ["Secure"] : []),
    ].join(";");
  } catch {
    // Cookies disabled. Attribution is lost for this visitor, which is the
    // correct outcome — it must not cost them the page.
  }
}

/**
 * Read the landing cookie, writing one for this arrival if there isn't one,
 * and updating it when this arrival carries a marketing handoff.
 *
 * @returns The landing record for this visitor, or null outside the browser
 */
export function ensureLandingCookie(): LandingCookieValue | null {
  if (typeof document === "undefined") {
    return null;
  }

  const existing = readLandingCookie();
  const handoff = readLandingHandoff(window.location.search);

  if (existing && !handoff) {
    return existing;
  }

  const value = existing
    ? mergeLandingHandoff(existing, handoff as LandingHandoff)
    : buildLandingCookieValue(newVisitorId(), new Date().toISOString());

  writeLandingCookie(value);

  return value;
}

/**
 * The source page and CTA, shaped as properties for the sign-up screen's
 * analytics events, so anonymous stages can be grouped by marketing page.
 *
 * Absent values are sent as `null` rather than omitted: an event with the key
 * missing is indistinguishable from one sent before the key existed.
 */
/**
 * Remember which sign-up button the visitor pressed, and Tolt's ids if
 * tolt.js captured a partner link, so the server can stitch both to the
 * account even when the auth provider carries nothing through.
 *
 * @param method - The button pressed on the sign-up screen
 */
export function rememberSignupChoice(method: "google" | "email"): void {
  const landing = ensureLandingCookie();
  if (!landing) return;

  const tolt = getToltSignupUnsafeMetadata()?.[TOLT_SIGNUP_METADATA_KEY];
  writeLandingCookie({
    ...landing,
    signupMethod: method,
    toltPartnerId: tolt?.partnerId ?? landing.toltPartnerId ?? null,
    toltClickId: tolt?.clickId ?? landing.toltClickId ?? null,
  });
}

export function landingEventProperties(): { page: string | null; cta: string | null } {
  // Ensure rather than read: the sign-up screen's own effects can run before
  // the root tracker's, and the first visit with a handoff must not report
  // the page as unknown.
  const landing = ensureLandingCookie();

  return {
    page: landing?.sourcePage ?? null,
    cta: landing?.sourceCta ?? null,
  };
}
